import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { GetServerSidePropsContext, NextPage } from "next";
import Head from "next/head";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { useAppDispatch, useAppSelector } from "custom-hooks/redux.hooks";
import { CPagesPath } from "shared/models/const/pages-path";
import Image from "next/image";
import { Avatar, Grid } from "@mui/material";
import Button from "@components/Button";
import { logout, setUser } from "store/feature/auth/auth.slice";
import {
  ClientUser,
  UserSocialAccounts,
} from "shared/models/interfaces/client-user.interface";
import Link from "next/link";
import ProfilePicture from "@components/profile-picture";
import { useQuery } from "@tanstack/react-query";
import { userGetProfile } from "api/clients/clients.request";
import CustomCarrusel from "@components/custom-carrusel";
import PropertyCardCollectionSkeleton from "@components/skeletons/property-collector-card.skeleton";
import { getFavorites } from "api/clients/favorites/favorites.request";
import useGetPersistParams from "custom-hooks/useGetPersintParamas.hook";
import useWindowSize from "custom-hooks/useWindowsSize.hook";
import PropertyCard from "@components/property-card";
import { css } from "@emotion/css";
import HeartBrokenIcon from "@mui/icons-material/HeartBroken";
import UserSettingsLayout from "shared/layouts/user-settings";
import { en, es } from "@lang";
import LoadingBubbles from "@components/loading-bubbles";
export async function getServerSideProps({
  locale,
}: GetServerSidePropsContext) {
  return {
    props: {
      messages: locale === "es" ? es : en,
    },
  };
}

function ProfilePage(props: NextPage) {
  const titleT = useTranslations("title-pages");
  const t = useTranslations("ProfilePage");
  const router = useRouter();
  const _user = useAppSelector((state) => state.Auth.user);
  const [userData, setUserData] = useState<ClientUser | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    setUserData(_user);
    if (!_user) router.push(CPagesPath.es.signin);
  }, [_user, router]);

  useQuery(["user/profile"], userGetProfile, {
    staleTime: Infinity,
    cacheTime: Infinity,
    onSuccess: (data) => {
      if (data) setUserCb(data);
    },
  });
  const size = useWindowSize();
  const width: any = size?.width;
  const ref = useGetPersistParams();

  const { isLoading, data: favorites } = useQuery(
    ["favorites"],
    () => getFavorites({ page: 1 }),
    {
      cacheTime: Infinity,
    }
  );

  const setUserCb = (data: ClientUser) => {
    dispatch(setUser(data));
    setUserData(data);
  };

  if (!userData) return <LoadingBubbles center message="Cargando" />;

  const socialNetworks = userData.social_networks;

  return (
    <>
      <Head>
        <title>{titleT("profile")}</title>
      </Head>
      <Main>
        <Header>
          <h1>{t("title")}</h1>
        </Header>
        <UserDataContainer>
          <Grid container className={dataBanner}>
            <Grid item>
              <ProfilePicture src={userData.profile_picture} size={225} />
            </Grid>
            <Grid item>
              <UserData>
                <Avatar
                  src="/images/v2/logos/7.png"
                  alt="Logo Re/max"
                  sx={{
                    width: 150,
                    height: "auto",
                    objectFit: "contain",
                    margin: "5px 0",
                  }}
                  variant="square"
                />
                <hr />
                <h2>{userData.full_name}</h2>
                <UserProfileDataFields>
                  <UserProfileDataField>
                    <strong>Whatsapp: </strong>
                    <span>{userData.social_networks?.whatsapp}</span>
                  </UserProfileDataField>
                  <UserProfileDataField>
                    <strong>{t("labels.user-data.phone")}: </strong>
                    <span>{userData.phone}</span>
                  </UserProfileDataField>
                  <UserProfileDataField>
                    <strong>{t("labels.user-data.email")}: </strong>
                    <span>{userData.email}</span>
                  </UserProfileDataField>
                </UserProfileDataFields>
              </UserData>
            </Grid>
            <Grid item>
              <ButtonsList>
                <Link href={CPagesPath.es.profileCambiarClave} passHref>
                  <Button
                    thin_button
                    fullWidth
                    className={noWrap}
                    sx={{ maxWidth: 200, mx: "auto", mb: 1 }}
                    bg="--color-brand-grey-secondary"
                  >
                    {t("links.change-password")}
                  </Button>
                </Link>
                <Link href={CPagesPath.es.profileEditar} passHref>
                  <Button
                    thin_button
                    fullWidth
                    sx={{ maxWidth: 200, mx: "auto", mb: 1 }}
                    bg="--color-brand-blue-main"
                  >
                    {t("links.edit-profile")}
                  </Button>
                </Link>
                <Button
                  thin_button
                  fullWidth
                  sx={{ maxWidth: 200, mx: "auto", mb: 1 }}
                  onClick={() => dispatch(logout())}
                >
                  {t("buttons.logout")}
                </Button>
              </ButtonsList>
              <section className="social-networks">
                {socialNetworks &&
                  (
                    Object.keys(socialNetworks) as (keyof UserSocialAccounts)[]
                  ).map((key, i) => {
                    return key === "whatsapp"
                      ? null
                      : socialNetworks[key] && (
                          <a
                            href={socialNetworks[key]}
                            key={i}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              src={`/icons/${key}_icon_gray.svg`}
                              alt={key + " icon"}
                            />
                          </a>
                        );
                  })}
              </section>
            </Grid>
          </Grid>
        </UserDataContainer>
        <FavoritesSection
          className={favorites && !favorites.data.length ? "no-favorites" : ""}
        >
          <SubHeader>{t("sub_headers.favorites")}</SubHeader>
          <CustomCarrusel
            enablePlaceholder
            placeholderItem={<PropertyCardCollectionSkeleton />}
            numberOfPlaceholderItems={4}
            forcePlaceHolder={!favorites && isLoading}
          >
            {favorites &&
              favorites.data.map((property: any, i: number) => (
                <Link
                  key={i}
                  href={CPagesPath.es.property_detail(property) + ref}
                  passHref
                >
                  <CardWrapper key={i} target={width > 991 ? "_blank" : ""}>
                    <PropertyCard {...property}></PropertyCard>
                  </CardWrapper>
                </Link>
              ))}
          </CustomCarrusel>
          {favorites && favorites.meta.total > 0 ? (
            <p className={linkToFavs}>
              <Link href={CPagesPath.es.profileFavorites}>
                {t("links.view-all-favorites")}
              </Link>
            </p>
          ) : (
            <NoFavorites>
              <span>
                {t("labels.no-favorites")}
                <br />
                <HeartBrokenIcon />
              </span>
            </NoFavorites>
          )}
        </FavoritesSection>
      </Main>
    </>
  );
}

ProfilePage.Layout = UserSettingsLayout;
export default ProfilePage;

//#region Styles
const ButtonsList = styled.section`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const noWrap = css`
  white-space: nowrap;
`;

const NoFavorites = styled.div`
  display: grid;
  place-content: center;
  margin-top: 30px;
  text-align: center;

  span {
    color: var(--color-brand-blue-main);
    font-weight: bold;
  }
`;

const Main = styled.div`
  display: grid;
  grid-template-rows: 90px minmax(338px, auto) 1fr;
  grid-row-gap: 20px;
  min-height: var(--main-height);
`;

const Header = styled.section`
  display: grid;
  place-content: center;
  margin-top: 20px;

  h1 {
    color: var(--color-brand-blue-main);
    text-transform: uppercase;
  }
`;

const UserDataContainer = styled.section`
  display: grid;
  background-color: white;
  justify-items: center;
  width: 100%;

  .buttons-list {
    margin-top: 20px;

    & > .MuiButtonBase-root {
      margin-bottom: 5px;
    }
  }

  .social-networks {
    margin-top: 10px;
    display: grid;
    grid-template-columns: repeat(auto-fit, 40px);
    justify-content: center;
    grid-gap: 10px;
    img {
      width: 30px;
      height: 30px;
    }
  }
`;

const dataBanner = css`
  display: grid;
  max-width: 800px;
  margin: auto;
  grid-template-columns: minmax(0, 225px) 2fr 1fr;
  grid-gap: 10px;
  padding: 0 20px;
  align-items: center;

  @media (max-width: 599px) {
    grid-template-columns: 1fr;
    justify-items: center;
    margin: 30px 0;
  }
`;

const UserData = styled.div`
  padding-top: 10px;
  margin-left: 10px;

  hr {
    max-width: 160px;
  }

  h2 {
    margin: 16px 0 20px;
    font-size: 20px;
    text-align: left;
    color: var(--color-brand-blue-main);
  }
`;

const UserProfileDataFields = styled.div`
  & > p {
    margin-bottom: 10px;
  }
`;

const UserProfileDataField = styled.p`
  font-weight: bold;
  font-size: 17px;

  span {
    color: var(--color-brand-grey-secondary);
  }
`;

const FavoritesSection = styled.section`
  margin: auto;
  margin-top: 20px;
  max-width: var(--list-max-width);
  width: calc(100vw - 270px);

  &.no-favorites {
    display: grid;
    place-content: center;
    grid-template-columns: 1fr;
    grid-template-rows: 40px 0px 1fr;
  }

  @media (max-width: 899px) {
    width: calc(100vw - 40px);
  }
`;

const SubHeader = styled.h3`
  color: var(--color-brand-blue-main);
  text-align: center;
  margin-bottom: 20px;
`;

const CardWrapper = styled.a`
  display: flex;
  justify-content: center;
  max-width: 100%;
  margin: 5px;
`;

const linkToFavs = css`
  text-align: center;
  margin: 40px;
  font-weight: 600;

  a {
    color: var(--color-brand-grey-secondary);

    &:hover {
      text-decoration: underline;
    }
  }
`;
//#endregion
